import type { Dispatch } from 'redux';
import {
    HubPersona,
    Person,
    DeletedPersona,
    createHubPersona,
    createDataActionFactory,
    Feature,
    hashFolderId,
    createThunk,
    isSupportedPersona,
    getLogger,
    getOwsPersona,
    ThunkDispatch,
    getLogSafeId,
    OwsError,
    PersonType,
    rootContactFolderDistinguishedName,
} from '../../../shared';
import { getPackageState } from '../../../appstate';
import { findPeopleWithRequestTracking } from '../../dataLayer/ows/findPeopleWithRequestTracking';
import { getListContacts } from '../../dataLayer/peopleGraph/getListContacts';
import getSuggestedPersonas from '../../dataLayer/ows/getSuggestedPersonas';
import { getDeletedContactsData } from '../../dataLayer/ows/getDeletedContactsData';
import { HubState, packageStateKey } from './../../state';
import { getFindPeopleSortingPath } from '../../utils/personaListSortUtils';
import { updateContactStatistics, ContactStatistics } from '../../utils/contactsStatistics';
import { getGuid } from 'owa-guid';
import { subscribeToFolderNotifications } from './notificationActions';

const INITIAL_ROOT_FOLDER_BATCH_SIZE = 20;
const BATCH_SIZE = 500;

const logSafePersonaId = (personaId: string) => ({
    personaId: getLogSafeId(personaId),
    length: personaId.length,
});

export type { Person };

export const loadPersonasCompletedAction = createDataActionFactory<
    {
        personas: HubPersona[];
        folderId: string;
        batchId: string;
        isFirstBatch: boolean;
        isLastBatch: boolean;
    },
    { error: OwsError; folderId: string }
>(
    'LoadPersonasCompletedAction',
    Feature.ContactManagement,
    action => ({
        folderId: hashFolderId(action.payload.folderId),
        count: action.payload.personas ? action.payload.personas.length : 0,
    }),
    action => ({
        folderId: hashFolderId(action.payload.folderId),
    })
);

export const loadSuggestedPersonasCompletedAction = createDataActionFactory<
    { suggestedPersonas: Person[] },
    Error
>('LoadSuggestedPersonasCompletedAction', Feature.ContactManagement, action => {
    return {
        count: action.payload.suggestedPersonas ? action.payload.suggestedPersonas.length : 0,
    };
});
export const getPersonaCompletedAction = createDataActionFactory<
    { persona: HubPersona },
    { error: Error; personaId: string }
>('GetPersonaCompletedAction', Feature.ContactManagement);

export const loadSuggestedPersonas = (dispatch: Dispatch) =>
    getSuggestedPersonas().then(
        suggestedPersonas =>
            dispatch(loadSuggestedPersonasCompletedAction.success({ suggestedPersonas })),
        error => dispatch(loadSuggestedPersonasCompletedAction.error(error))
    );

export const deletedContactsRetrieveCompletedAction = createDataActionFactory<
    {
        deletedItemsContacts: DeletedPersona[];
        recoverableItemsContacts: DeletedPersona[];
        folderIds: string[];
    },
    { error: Error }
>('DeletedContactsRetrieveCompletedAction', Feature.RestoreDeletedContacts, action => {
    return {
        countDeleted: action.payload.deletedItemsContacts
            ? action.payload.deletedItemsContacts.length
            : 0,
        countRecoverable: action.payload.recoverableItemsContacts
            ? action.payload.recoverableItemsContacts.length
            : 0,
    };
});

export const loadDeletedItemsFolder = (dispatch: ThunkDispatch) =>
    getDeletedContactsData()
        .then(deletedContactsData => {
            dispatch(
                deletedContactsRetrieveCompletedAction.success({
                    deletedItemsContacts: deletedContactsData.deletedItems,
                    recoverableItemsContacts: deletedContactsData.recoverableItems,
                    folderIds: deletedContactsData.folderIds,
                })
            );
            deletedContactsData.folderIds.map(folderId => {
                subscribeToFolderNotifications(folderId, dispatch);
            });
        })
        .catch((error: Error) => dispatch(deletedContactsRetrieveCompletedAction.error({ error })));

const getPersonasInFolderInternal = (
    contactFolderId: string,
    isInitialLoadOfRootFolder: boolean = false,
    isExemptFromCurrentRequestCheck: boolean = false,
    offset: number = 0,
    limit: number = BATCH_SIZE
) =>
    createThunk('GetPersonasInFolderInternal', (dispatch: ThunkDispatch, getState: () => {}) => {
        // State is retrieved before the chain of fetches, and passed via constants in closure
        const batchId = getGuid();

        const state = getPackageState<HubState>(getState(), packageStateKey);
        const sortingPath = getFindPeopleSortingPath(state.appState);
        const isCAPIv3Enabled = state.appState.view.layoutState.isCAPIv3Enabled;
        let allPersonasInFolder = [];

        // This method is called once, then recursively more times if we figure out that we may have more people to fetch
        // Mark and Free requests that we make, to ensure there are never two identical pending requests
        const findPeopleChain = (currentOffset: number, contactStatistics: ContactStatistics) => {
            // !!!Performance optimization special case!!!:
            // If we are loading the first chunk (offset === 0) of the PLT query (isInitialLoadOfRootFolder),
            // lower the batch size temporarily so that we can provide above-the-fold data faster
            const currentLimit =
                isInitialLoadOfRootFolder && currentOffset === 0
                    ? INITIAL_ROOT_FOLDER_BATCH_SIZE
                    : limit;

            let fetchRequests = [];

            fetchRequests.push(
                findPeopleWithRequestTracking(
                    contactFolderId,
                    currentOffset,
                    currentLimit,
                    sortingPath,
                    isExemptFromCurrentRequestCheck,
                    false /* isSparseFolder */
                )
            );

            if (isCAPIv3Enabled) {
                fetchRequests.push(getListContacts());
            }

            return Promise.all(fetchRequests).then(
                result => {
                    const findPeopleResults = result[0];
                    const { personas, getIsCurrentRequest } = findPeopleResults;

                    if (isCAPIv3Enabled) {
                        // We need to merge the results of ListContacts CAPIv3
                        // with FindPeople to get the contact lists along with
                        // contacts, since the store currently expects both
                        // contacts and contact-lists in one action.

                        allPersonasInFolder = [];
                        allPersonasInFolder = allPersonasInFolder.concat(result[1]);
                        const contactLists = personas
                            .filter(isSupportedPersona)
                            .filter(
                                persona =>
                                    persona.type === PersonType.PrivateDistributionList ||
                                    persona.type === PersonType.PublicDistributionList
                            );
                        allPersonasInFolder = allPersonasInFolder.concat(contactLists);
                    } else {
                        allPersonasInFolder = allPersonasInFolder.concat(
                            personas.filter(isSupportedPersona)
                        );
                    }
                    const hasMorePersonas =
                        personas.length > 0 && personas.length % currentLimit === 0;
                    dispatch(
                        loadPersonasCompletedAction.success({
                            folderId: contactFolderId,
                            personas: allPersonasInFolder,
                            batchId: batchId,
                            isFirstBatch: currentOffset === 0,
                            isLastBatch: !hasMorePersonas,
                        })
                    );

                    // For the initial load of the root contact folder, we count the various contact sources and the amount of old OWS favorites
                    if (isInitialLoadOfRootFolder) {
                        updateContactStatistics(personas, contactStatistics);
                    }

                    // If the amount of personas returned is a multiple of the batch size, there can be more
                    // personas on the server, issue a new request with the new offset unless there has been a subsequent request
                    if (
                        hasMorePersonas &&
                        (isExemptFromCurrentRequestCheck || getIsCurrentRequest())
                    ) {
                        return findPeopleChain(currentOffset + currentLimit, contactStatistics);
                    } else if (isInitialLoadOfRootFolder) {
                        logRootContactFolderStatistics(contactStatistics);
                    }
                },
                error =>
                    dispatch(
                        loadPersonasCompletedAction.error({ error, folderId: contactFolderId })
                    )
            );
        };
        return findPeopleChain(offset, {
            contactSourceCounts: {},
            contactsPerPersonaCounts: {},
            jsMvvMFavoritesCount: 0,
            rogueRecipientCacheContactCounts: {},
        });
    });

export const getInitialPersonasInRootFolder = (dispatch: ThunkDispatch) => (
    offset: number = 0,
    limit: number = BATCH_SIZE
) =>
    dispatch(
        getPersonasInFolderInternal(
            rootContactFolderDistinguishedName,
            true /* isInitialLoadOfRootFolder */,
            true /* isExemptFromCurrentRequestCheck */,
            offset,
            limit
        )
    );

export const getPersonasInFolder = (dispatch: ThunkDispatch) => (
    contactFolderId: string,
    isExemptFromCurrentRequestCheck: boolean = false,
    offset: number = 0,
    limit: number = BATCH_SIZE
) =>
    dispatch(
        getPersonasInFolderInternal(
            contactFolderId,
            false /* isInitialLoadOfRootFolder */,
            isExemptFromCurrentRequestCheck,
            offset,
            limit
        )
    );

export const getPersona = (dispatch: Dispatch) => (personaId: string, contactFolderId: string) =>
    getOwsPersona(personaId, contactFolderId, logSafePersonaId(personaId)).then(
        personaType => {
            const persona = createHubPersona(personaType) as HubPersona;
            dispatch(getPersonaCompletedAction.success({ persona }));
        },
        error => dispatch(getPersonaCompletedAction.error({ personaId, error }))
    );

function logRootContactFolderStatistics(contactStatistics: ContactStatistics): void {
    // When loading of the root contact folder is complete, log the count of
    // the various contact sources and the number of OWS favorites.
    const logger = getLogger();
    logger.log('MyContactsFolderContactSources', contactStatistics.contactSourceCounts);

    logger.log('ContactsPerPersonaCount', contactStatistics.contactsPerPersonaCounts);

    logger.log('jsMVVMFavoritesCount', { FavoritesCount: contactStatistics.jsMvvMFavoritesCount });

    // We want to log the frequence of rogue Recipient Cache contacts, to see how big the problem is,
    // and give input to the CAR team. When we have enough data, this logging can be removed. Search
    // for rogueRecipientCacheContact to find related code that can be removed.
    logger.log(
        'RogueRecipientCacheContactPersonas',
        contactStatistics.rogueRecipientCacheContactCounts
    );
}
export type { DataActionFactory } from '../../../shared';
export type { Action } from '../../../shared';
